import React, { useRef, useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import './GalerijaKlinikeV2.css';

const GALLERY_IMAGES = [
  "./images/galerijaKlinike/prijem.jpg",
  "./images/galerijaKlinike/pregledavaona2.jpg",
  "./images/galerijaKlinike/pregledavaona1.jpg",
  "./images/galerijaKlinike/ultrazvuk3.jpg",
  "./images/galerijaKlinike/ultrazvuk4.jpg",
  "./images/galerijaKlinike/lab4.jpg",
  "./images/galerijaKlinike/rengen2.jpg",
  "./images/galerijaKlinike/rengen1.jpg",
  "./images/galerijaKlinike/kirurskaDvorana.jpg",
  "./images/galerijaKlinike/op2.jpg",
  "./images/galerijaKlinike/op3.jpg",
];

const SCROLL_AMOUNT = 420;

export default function GalerijaKlinikeV2() {
  const galleryRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const infiniteGalleryImages = [...GALLERY_IMAGES, ...GALLERY_IMAGES, ...GALLERY_IMAGES];
  const originalLength = GALLERY_IMAGES.length;

  const closeModal = useCallback(() => {
    setModalOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  const nextImage = useCallback((e) => {
    if (e) e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === GALLERY_IMAGES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const prevImage = useCallback((e) => {
    if (e) e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === 0 ? GALLERY_IMAGES.length - 1 : prev - 1
    );
  }, []);

  useEffect(() => {
    if (!galleryRef.current) return;
    
    const container = galleryRef.current;
    const firstChild = container.children[0];
    if (!firstChild) return;

    const itemWidth = firstChild.offsetWidth + 20;
    const initialScroll = originalLength * itemWidth;
    container.scrollLeft = initialScroll;

    const handleScroll = () => {
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const currentScroll = container.scrollLeft;
      
      if (currentScroll >= scrollWidth - clientWidth - 10) {
        container.scrollLeft = initialScroll + (currentScroll - (scrollWidth - clientWidth - 10));
      }
      else if (currentScroll <= 10) {
        container.scrollLeft = initialScroll - (10 - currentScroll);
      }
    };
    
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [originalLength]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!modalOpen) return;
      
      if (e.key === 'ArrowRight') {
        nextImage(e);
      } else if (e.key === 'ArrowLeft') {
        prevImage(e);
      } else if (e.key === 'Escape') {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, nextImage, prevImage, closeModal]);

  const scrollGallery = useCallback((direction) => {
    if (!galleryRef.current) return;
    
    const scrollAmount = direction === "left" ? -SCROLL_AMOUNT : SCROLL_AMOUNT;
    galleryRef.current.scrollBy({
      left: scrollAmount,
      behavior: "smooth"
    });
  }, []);

  const openModal = useCallback((index) => {
    setCurrentImageIndex(index);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  return (
    <>
      <main className="gallery-clinic-container">
        <Helmet>
          <title>Galerija klinike | Veterinarski centar</title>
          <meta 
            name="description" 
            content="Pogledajte galeriju naše veterinarske klinike, moderne prostorije, opremu i uvjete za njegu vaših ljubimaca."
          />
          <meta name="keywords" content="veterinar, galerija, klinika, ljubimci, oprema, ambulanta" />
          <meta property="og:title" content="Galerija klinike | Veterinarski centar" />
          <meta property="og:description" content="Pogledajte našu veterinarsku kliniku i opremu." />
          <meta property="og:type" content="website" />
        </Helmet>

        <section className="gallery-clinic-section">
          <header className="gallery-clinic-header">
            <h2 className="gallery-clinic-title">Galerija klinike</h2>
          </header>

          <div className="gallery-clinic-wrapper">
            <button 
              className="gallery-clinic-button prev" 
              onClick={() => scrollGallery("left")}
              aria-label="Pomakni galeriju lijevo"
            >
              <FaChevronLeft />
            </button>

            <div ref={galleryRef} className="gallery-clinic-scroll" role="list">
              {infiniteGalleryImages.map((img, index) => (
                <div 
                  key={`clinic-gallery-${index}`} 
                  className="gallery-clinic-item"
                  onClick={() => {
                    const actualIndex = index % originalLength;
                    openModal(actualIndex);
                  }}
                  role="listitem"
                >
                  <img 
                    src={img} 
                    alt={`Veterinarska klinika - slika ${(index % originalLength) + 1}`}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>

            <button 
              className="gallery-clinic-button next" 
              onClick={() => scrollGallery("right")}
              aria-label="Pomakni galeriju desno"
            >
              <FaChevronRight />
            </button>
          </div>
        </section>

        <div 
          className={`gallery-clinic-modal ${modalOpen ? 'active' : ''}`} 
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label="Galerija slika"
        >
          <button 
            className="gallery-clinic-modal-close" 
            onClick={closeModal}
            aria-label="Zatvori galeriju"
          >
            <FaTimes />
          </button>
          
          {GALLERY_IMAGES.length > 1 && (
            <>
              <button 
                className="gallery-clinic-modal-nav gallery-clinic-modal-prev" 
                onClick={prevImage}
                aria-label="Prethodna slika"
              >
                <FaChevronLeft />
              </button>
              <button 
                className="gallery-clinic-modal-nav gallery-clinic-modal-next" 
                onClick={nextImage}
                aria-label="Sljedeća slika"
              >
                <FaChevronRight />
              </button>
            </>
          )}
          
          <img 
            src={GALLERY_IMAGES[currentImageIndex]} 
            alt={`Galerija ${currentImageIndex + 1}`}
            className="gallery-clinic-modal-content"
            onClick={(e) => e.stopPropagation()}
          />
          
          <div className="gallery-clinic-modal-counter">
            {currentImageIndex + 1} / {GALLERY_IMAGES.length}
          </div>
        </div>
      </main>
    </>
  );
}